import { apiClient } from '@/services/api-client';
import {
  ProviderCustomer,
  ProviderCustomerCreateInput,
  ProviderCustomerDetail,
  ProviderCustomerListParams,
  ProviderCustomerUpdateInput,
  ProviderCustomersResult,
  ProviderCustomerStatus,
  ProviderCustomerStatusOption,
  ProviderCustomerTeamRole,
  ProviderCustomerDeleteInput,
  ProviderCustomerDeleteResult,
  ProviderCustomersMeta,
} from '@/types/provider-customers';

interface ProviderCustomerResourceLike {
  id: string;
  type?: string;
  attributes?: Record<string, unknown>;
  [key: string]: unknown;
}

interface ProviderCustomersApiPayload {
  data: ProviderCustomerResourceLike[];
  meta?: Record<string, unknown>;
  status_options?: ProviderCustomerStatusOption[];
  statusOptions?: ProviderCustomerStatusOption[];
}

export interface ProviderCustomerCreateResult {
  customer: ProviderCustomer;
  signupRedirectUrl?: string | null;
  signupRedirectToken?: string | null;
  meta?: Record<string, unknown>;
}

const DEFAULT_STATUS_OPTIONS: ProviderCustomerStatusOption[] = [
  { value: '', label: 'All Status' },
  { value: 'active', label: 'Active' },
  { value: 'inactive', label: 'Inactive' },
  { value: 'trial', label: 'Trial' },
  { value: 'suspended', label: 'Suspended' },
  { value: 'pending_verification', label: 'Pending Verification' },
];

const STATUS_LABELS: Record<ProviderCustomerStatus, string> = {
  active: 'Active',
  inactive: 'Inactive',
  trial: 'Trial',
  suspended: 'Suspended',
  pending_verification: 'Pending Verification',
};

const normalizeStatus = (status: unknown): ProviderCustomerStatus => {
  if (typeof status !== 'string') {
    return 'inactive';
  }

  if (
    status === 'active' ||
    status === 'inactive' ||
    status === 'trial' ||
    status === 'suspended' ||
    status === 'pending_verification'
  ) {
    return status;
  }

  return 'inactive';
};

const readAttributes = (resource: ProviderCustomerResourceLike): Record<string, unknown> => {
  if (resource.attributes && typeof resource.attributes === 'object') {
    return resource.attributes;
  }
  return resource;
};

const readString = (value: unknown): string => (typeof value === 'string' ? value : '');
const readNullableString = (value: unknown): string | null => (typeof value === 'string' ? value : null);
const readBoolean = (value: unknown, fallback = false): boolean => (typeof value === 'boolean' ? value : fallback);
const readNumber = (value: unknown, fallback = 0): number => (typeof value === 'number' ? value : fallback);
const readRecord = (value: unknown): Record<string, unknown> | null =>
  value && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;

const TEAM_ROLE_VALUES: ProviderCustomerTeamRole['role'][] = ['owner', 'member', 'individual'];

const toTeamRoles = (rawRoles: unknown): ProviderCustomerTeamRole[] => {
  if (!Array.isArray(rawRoles)) {
    return [];
  }

  const dedupedRoles = new Map<string, ProviderCustomerTeamRole>();

  rawRoles.forEach((role) => {
    if (!role || typeof role !== 'object') {
      return;
    }

    const roleObject = role as Record<string, unknown>;
    const rawRoleValue = readString(roleObject.role ?? roleObject.role_type);
    const roleValue = TEAM_ROLE_VALUES.includes(rawRoleValue as ProviderCustomerTeamRole['role'])
      ? (rawRoleValue as ProviderCustomerTeamRole['role'])
      : 'individual';
    const icon = readString(roleObject.icon) || (roleValue === 'owner' ? '👑' : roleValue === 'member' ? '👥' : '👤');
    const teamName = readNullableString(roleObject.team_name ?? roleObject.teamName);
    const display = readString(roleObject.display);

    const statusValue = normalizeStatus(roleObject.status);
    const joinedAtValue = readNullableString(roleObject.joined_at ?? roleObject.joinedAt);

    const teamRole: ProviderCustomerTeamRole = {
      teamId: readNullableString(roleObject.team_id ?? roleObject.teamId),
      teamName,
      role: roleValue || 'individual',
      icon,
      display:
        display ||
        `${icon} ${roleValue ? roleValue.charAt(0).toUpperCase() + roleValue.slice(1) : 'Individual'}${
          teamName ? `: ${teamName}` : ''
        }`,
    };

    if (statusValue) {
      teamRole.status = statusValue;
    }

    if (joinedAtValue) {
      teamRole.joinedAt = joinedAtValue;
    }

    const key = `${teamRole.teamId ?? 'individual'}:${teamRole.role}`;
    const existing = dedupedRoles.get(key);

    if (!existing) {
      dedupedRoles.set(key, teamRole);
      return;
    }

    // Merge any additional metadata we might have missed on the first occurrence.
    if (!existing.status && teamRole.status) {
      existing.status = teamRole.status;
    }
    if (!existing.joinedAt && teamRole.joinedAt) {
      existing.joinedAt = teamRole.joinedAt;
    }
    if (!existing.teamName && teamRole.teamName) {
      existing.teamName = teamRole.teamName;
    }
    if (!existing.display && teamRole.display) {
      existing.display = teamRole.display;
    }
  });

  return Array.from(dedupedRoles.values());
};

const toMeta = (meta: Record<string, unknown> | undefined, fallbackCount: number): ProviderCustomersMeta => {
  if (!meta) {
    return {
      currentPage: 1,
      perPage: fallbackCount,
      total: fallbackCount,
      lastPage: 1,
      from: fallbackCount > 0 ? 1 : 0,
      to: fallbackCount,
    };
  }

  const pageMeta = (meta.page as Record<string, unknown> | undefined) ?? {};
  const currentPage = readNumber(
    meta.current_page ?? meta.currentPage ?? pageMeta.number,
    1,
  );
  const perPage = readNumber(
    meta.per_page ?? meta.perPage ?? pageMeta.size,
    fallbackCount || 10,
  );
  const total = readNumber(meta.total, fallbackCount);
  const count = readNumber(pageMeta.count, fallbackCount);
  const lastPage = readNumber(
    meta.last_page ?? meta.lastPage,
    Math.max(1, Math.ceil(total / (perPage || 1))),
  );
  const fromValue = readNumber(meta.from, total > 0 ? (currentPage - 1) * perPage + 1 : 0);
  const toValue = readNumber(meta.to, total > 0 ? fromValue + Math.max(0, count - 1) : 0);

  return {
    currentPage,
    perPage,
    total,
    lastPage,
    from: fromValue,
    to: toValue,
  };
};

const mapParamsToQuery = (params?: ProviderCustomerListParams): Record<string, unknown> | undefined => {
  if (!params) {
    return undefined;
  }

  const query: Record<string, unknown> = {};

  if (params.search) {
    query.search = params.search;
  }

  if (params.teamRole) {
    query.team_role = params.teamRole;
  }

  if (params.status) {
    query.status = params.status;
  }

  if (params.teamId) {
    query.team_id = params.teamId;
  }

  if (params.sort) {
    query.sort = params.sort;
  }

  if (typeof params.page === 'number') {
    query['page[number]'] = params.page;
    query.page = params.page;
  }

  if (typeof params.perPage === 'number') {
    query['page[size]'] = params.perPage;
    query.per_page = params.perPage;
  }

  return query;
};

const toProviderCustomerBase = (resource: ProviderCustomerResourceLike): {
  base: ProviderCustomer;
  attributes: Record<string, unknown>;
} => {
  const attributes = readAttributes(resource);
  const status = normalizeStatus(attributes.status);
  const statusLabel = readString(attributes.status_label ?? attributes.statusLabel) || STATUS_LABELS[status];
  const teamRoles = toTeamRoles(attributes.team_roles ?? attributes.teamRoles);

  const isTeamOwner = readBoolean(attributes.is_team_owner ?? attributes.isTeamOwner, teamRoles.some((role) => role.role === 'owner'));
  const teamsCount = readNumber(attributes.teams_count ?? attributes.teamsCount, teamRoles.filter((role) => role.role !== 'individual').length);
  const trialEndsAt = readNullableString(attributes.trial_ends_at ?? attributes.trialEndsAt);
  const trialAssignedDaysRaw = attributes.trial_assigned_days ?? attributes.trialAssignedDays;
  const trialRemainingDaysRaw = attributes.trial_remaining_days ?? attributes.trialRemainingDays;
  const trialAssignedDays =
    typeof trialAssignedDaysRaw === 'number'
      ? trialAssignedDaysRaw
      : typeof trialAssignedDaysRaw === 'string'
        ? Number(trialAssignedDaysRaw)
        : null;
  const trialRemainingDays =
    typeof trialRemainingDaysRaw === 'number'
      ? trialRemainingDaysRaw
      : typeof trialRemainingDaysRaw === 'string'
        ? Number(trialRemainingDaysRaw)
        : null;

  const base: ProviderCustomer = {
    id: readString(resource.id),
    name: readString(attributes.name),
    email: readString(attributes.email),
    contactPhone: readNullableString(attributes.contact_phone ?? attributes.contactPhone),
    companyName: readNullableString(attributes.company_name ?? attributes.companyName),
    active: readBoolean(attributes.active, status === 'active'),
    status,
    statusLabel,
    trialEndsAt,
    isTrial: readBoolean(attributes.is_trial ?? attributes.isTrial, status === 'trial'),
    trialAssignedDays: Number.isFinite(trialAssignedDays) ? trialAssignedDays : null,
    trialRemainingDays: Number.isFinite(trialRemainingDays) ? trialRemainingDays : null,
    createdAt: readString(attributes.created_at ?? attributes.createdAt),
    updatedAt: readString(attributes.updated_at ?? attributes.updatedAt ?? attributes.created_at ?? attributes.createdAt),
    teamRoles,
    isTeamOwner,
    teamsCount,
    language: readNullableString(attributes.language),
    deletedAt: readNullableString(attributes.deleted_at ?? attributes.deletedAt),
  };

  return { base, attributes };
};

const toProviderCustomer = (resource: ProviderCustomerResourceLike): ProviderCustomer => {
  return toProviderCustomerBase(resource).base;
};

const toProviderCustomerDetail = (resource: ProviderCustomerResourceLike): ProviderCustomerDetail => {
  const { base, attributes } = toProviderCustomerBase(resource);
  const billingAddress = readRecord(attributes.billing_address ?? attributes.billingAddress);

  return {
    ...base,
    phone: readNullableString(attributes.phone),
    timezone: readNullableString(attributes.timezone),
    notes: readNullableString(attributes.notes),
    lastLoginAt: readNullableString(attributes.last_login_at ?? attributes.lastLoginAt),
    tags: Array.isArray(attributes.tags)
      ? (attributes.tags as unknown[]).filter((tag): tag is string => typeof tag === 'string')
      : [],
    checkoutProfile: {
      company: readNullableString(attributes.company_name ?? attributes.companyName ?? attributes.company),
      contactPhone: readNullableString(attributes.contact_phone ?? attributes.contactPhone ?? attributes.phone),
      billingAddressLine1: readNullableString(
        attributes.billing_address_line_1 ??
          attributes.billingAddressLine1 ??
          (attributes as Record<string, unknown>).billingAddressLine_1 ??
          (attributes as Record<string, unknown>).billing_address_line1 ??
          billingAddress?.address_line_1 ??
          billingAddress?.addressLine1 ??
          billingAddress?.addressLine_1 ??
          billingAddress?.line_1 ??
          billingAddress?.line1,
      ),
      billingAddressLine2: readNullableString(
        attributes.billing_address_line_2 ??
          attributes.billingAddressLine2 ??
          (attributes as Record<string, unknown>).billingAddressLine_2 ??
          (attributes as Record<string, unknown>).billing_address_line2 ??
          billingAddress?.address_line_2 ??
          billingAddress?.addressLine2 ??
          billingAddress?.addressLine_2 ??
          billingAddress?.line_2 ??
          billingAddress?.line2,
      ),
      billingAddressCity: readNullableString(
        attributes.billing_address_city ?? attributes.billingAddressCity ?? billingAddress?.city,
      ),
      billingAddressState: readNullableString(
        attributes.billing_address_state ?? attributes.billingAddressState ?? billingAddress?.state,
      ),
      billingAddressPostalCode: readNullableString(
        attributes.billing_address_postal_code ??
          attributes.billingAddressPostalCode ??
          billingAddress?.postal_code ??
          billingAddress?.postalCode,
      ),
      billingAddressCountry: readNullableString(
        attributes.billing_address_country ?? attributes.billingAddressCountry ?? billingAddress?.country,
      ),
    },
  };
};

const buildCreatePayload = (input: ProviderCustomerCreateInput) => {
  const payload: Record<string, unknown> = {
    name: `${(input.firstName ?? '').trim()} ${(input.lastName ?? '').trim()}`.trim(),
    email: input.email,
  };

  if (input.company) {
    payload.company_name = input.company;
  }
  if (input.phone) {
    payload.phone = input.phone;
  }
  if (input.timezone) {
    payload.timezone = input.timezone;
  }
  if (input.role) {
    payload.role = input.role;
  }
  if (input.teamName) {
    payload.team_name = input.teamName;
  }
  if (input.language) {
    payload.language = input.language;
  }
  if (typeof input.sendInvite === 'boolean') {
    payload.send_invite = input.sendInvite;
  }

  return payload;
};

const buildUpdatePayload = (input: ProviderCustomerUpdateInput) => {
  const payload: Record<string, unknown> = {
    name: `${(input.firstName ?? '').trim()} ${(input.lastName ?? '').trim()}`.trim(),
    status: input.status,
  };

  if (input.company !== undefined) {
    payload.company_name = input.company;
  }
  if (input.phone !== undefined) {
    payload.phone = input.phone;
  }
  if (input.timezone) {
    payload.timezone = input.timezone;
  }
  if (typeof input.twoFactorEnabled === 'boolean') {
    payload.two_factor_enabled = input.twoFactorEnabled;
  }

  return payload;
};

const extractResource = (apiData: unknown): ProviderCustomerResourceLike | null => {
  if (!apiData || typeof apiData !== 'object') {
    return null;
  }

  if ('data' in (apiData as Record<string, unknown>)) {
    const dataValue = (apiData as { data: unknown }).data;
    if (dataValue && typeof dataValue === 'object') {
      return dataValue as ProviderCustomerResourceLike;
    }
  }

  return apiData as ProviderCustomerResourceLike;
};

class ProviderCustomersService {
  private async listFromEndpoint(
    endpoint: string,
    params?: ProviderCustomerListParams,
  ): Promise<ProviderCustomersResult> {
    const query = mapParamsToQuery(params);
    const response = await apiClient.get<ProviderCustomersApiPayload>(endpoint, query ? { params: query } : undefined);

    const payload = response.data ?? { data: [] };
    const resources = Array.isArray(payload.data) ? payload.data : [];
    let customers = resources.map(toProviderCustomer);
    const rawMeta = payload.meta as Record<string, unknown> | undefined;
    let meta = toMeta(rawMeta, customers.length);

    if (!rawMeta) {
      const requestedPage = typeof params?.page === "number" ? params.page : 1;
      const requestedPerPage = typeof params?.perPage === "number" ? params.perPage : customers.length || 10;
      const total = customers.length;
      const lastPage = Math.max(1, Math.ceil(total / (requestedPerPage || 1)));
      const currentPage = Math.min(Math.max(1, requestedPage), lastPage);
      const from = total > 0 ? (currentPage - 1) * requestedPerPage + 1 : 0;
      const to = total > 0 ? Math.min(total, currentPage * requestedPerPage) : 0;

      customers = customers.slice(from > 0 ? from - 1 : 0, to);
      meta = {
        currentPage,
        perPage: requestedPerPage,
        total,
        lastPage,
        from,
        to,
      };
    }

    const statusOptions = payload.status_options ?? payload.statusOptions ?? DEFAULT_STATUS_OPTIONS;

    return {
      customers,
      meta,
      statusOptions: statusOptions.length ? statusOptions : DEFAULT_STATUS_OPTIONS,
    };
  }

  async list(params?: ProviderCustomerListParams): Promise<ProviderCustomersResult> {
    return this.listFromEndpoint("/api/v1/customers", params);
  }

  async listDeleted(params?: ProviderCustomerListParams): Promise<ProviderCustomersResult> {
    return this.listFromEndpoint("/api/v1/customers/deleted", params);
  }

  async create(input: ProviderCustomerCreateInput): Promise<ProviderCustomerCreateResult> {
    const payload = buildCreatePayload(input);
    const response = await apiClient.post<ProviderCustomersApiPayload | { data: ProviderCustomerResourceLike } | ProviderCustomerResourceLike>(
      "/api/v1/customers",
      payload
    );

    const resource = extractResource(response.data);
    if (!resource) {
      throw new Error("Invalid customer response");
    }

    const customer = toProviderCustomer(resource);
    const signupRedirectUrl = readNullableString(
      (response.meta as Record<string, unknown> | undefined)?.signup_redirect_url ??
      (response.meta as Record<string, unknown> | undefined)?.signupRedirectUrl,
    );
    const signupRedirectToken = readNullableString(
      (response.meta as Record<string, unknown> | undefined)?.signup_redirect_token ??
      (response.meta as Record<string, unknown> | undefined)?.signupRedirectToken,
    );

    return {
      customer,
      signupRedirectUrl,
      signupRedirectToken,
      meta: response.meta,
    };
  }

  async update(input: ProviderCustomerUpdateInput): Promise<ProviderCustomer> {
    const payload = buildUpdatePayload(input);
    const response = await apiClient.put<ProviderCustomersApiPayload | { data: ProviderCustomerResourceLike } | ProviderCustomerResourceLike>(
      `/api/v1/customers/${input.id}`,
      payload
    );

    const resource = extractResource(response.data);
    if (!resource) {
      throw new Error("Invalid customer response");
    }

    return toProviderCustomer(resource);
  }

  async get(customerId: string): Promise<ProviderCustomerDetail> {
    const response = await apiClient.get<ProviderCustomersApiPayload | { data: ProviderCustomerResourceLike } | ProviderCustomerResourceLike>(
      `/api/v1/customers/${customerId}`
    );

    const resource = extractResource(response.data);
    if (!resource) {
      throw new Error("Customer not found");
    }

    return toProviderCustomerDetail(resource);
  }

  async delete(input: ProviderCustomerDeleteInput): Promise<ProviderCustomerDeleteResult> {
    const body: Record<string, unknown> = { mode: input.mode };
    if (input.confirm) {
      body.confirm = input.confirm;
    }
    if (input.reason && input.reason.trim().length > 0) {
      body.reason = input.reason.trim();
    }

    const response = await apiClient.delete<Record<string, unknown>>(`/api/v1/customers/${input.id}`, { data: body });
    const responseMeta = (response.meta as Record<string, unknown> | undefined) ?? {};
    const payloadMeta = ((response.data as { meta?: Record<string, unknown> } | undefined)?.meta) ?? {};

    const message = readString(responseMeta.message ?? payloadMeta.message) || "Customer deleted successfully";
    const modeRaw = readString(responseMeta.mode ?? payloadMeta.mode);
    const mode = modeRaw === "hard" ? "hard" : "soft";
    const deletedAt = readNullableString(responseMeta.deleted_at ?? responseMeta.deletedAt ?? payloadMeta.deleted_at ?? payloadMeta.deletedAt);
    const email = readNullableString(responseMeta.email ?? payloadMeta.email);

    return {
      message,
      mode,
      deletedAt,
      email,
    };
  }

  async restore(customerId: string): Promise<ProviderCustomer> {
    const response = await apiClient.post<ProviderCustomersApiPayload | { data: ProviderCustomerResourceLike } | ProviderCustomerResourceLike>(
      `/api/v1/customers/${customerId}/restore`,
    );

    const resource = extractResource(response.data);
    if (!resource) {
      throw new Error("Invalid customer response");
    }

    return toProviderCustomer(resource);
  }
}

export const providerCustomersService = new ProviderCustomersService();
